import { createRouter, createWebHashHistory } from "@ionic/vue-router";
import { getUserInfo } from "@/service/account";
import store from "@/store/business";
import { isMiniProgramWebviewShell, navigateMiniProgramWebviewPath } from "@/utils/miniprogram-bridge";

const routeModules = import.meta.glob("./*.js", { eager: true });
const siteRoutes = Object.entries(routeModules)
    .filter(([path]) => path !== "./index.js")
    .flatMap(([, mod]) => mod.default || []);

const routes = [
    {
        path: "/",
        name: "index",
        component: () => import("@/views/Index.vue"),
    },
    ...siteRoutes,
    {
        path: "/forbidden",
        name: "NotAuthenticated",
        component: () => import("@/views/_/NotAuthenticated.vue"),
    },
    { path: "/:pathMatch(.*)*", name: "NotFound", component: () => import("@/views/_/NotFound.vue") },
];

const router = createRouter({
    history: createWebHashHistory(import.meta.env.BASE_URL),
    routes,
});

let userInfoLoadedToken = "";
const rawRouterPush = router.push.bind(router);

function getHashQuery() {
    if (typeof window === "undefined") return "";
    const hash = window.location.hash || "";
    const queryStart = hash.indexOf("?");
    if (queryStart === -1) return "";

    return hash.slice(queryStart + 1);
}

function getTokenFromLocation() {
    try {
        return (
            new URLSearchParams(window.location.search || "").get("token") ||
            new URLSearchParams(getHashQuery()).get("token") ||
            ""
        );
    } catch (error) {
        return "";
    }
}

function shouldOpenMiniProgramWebview(to) {
    if (!isMiniProgramWebviewShell()) return false;

    const from = router.currentRoute.value;
    if (!from?.matched?.length) return false;
    if (!to?.fullPath || to.fullPath === from.fullPath) return false;
    if (to.name === "NotAuthenticated" || to.name === "profile") return false;
    return true;
}

router.push = async function pushWithMiniProgramWebview(location) {
    const resolved = router.resolve(location);

    if (shouldOpenMiniProgramWebview(resolved)) {
        const opened = await navigateMiniProgramWebviewPath(resolved.fullPath);
        if (opened) {
            return;
        }
    }

    return rawRouterPush(location);
};

router.beforeEach(async (to) => {
    const cachedToken = localStorage.getItem("token") || "";
    const queryToken = to?.query?.token || "";
    const locationToken = getTokenFromLocation();
    const token = queryToken || locationToken || cachedToken;

    if (token && token !== cachedToken) {
        store.commit("setToken", token);
    }

    if (!token || to.name === "profile") {
        return true;
    }

    let user = store.state.user || {};
    const hasUser = Object.keys(user).length > 0;
    const shouldFetchUser = !hasUser || userInfoLoadedToken !== token;

    if (shouldFetchUser) {
        try {
            const res = await getUserInfo(token);
            user = res?.data?.data || {};
            store.commit("SET_USER_INFO", user);
            userInfoLoadedToken = token;
        } catch (error) {
            userInfoLoadedToken = "";
            store.commit("logout");
            return { name: "NotAuthenticated" };
        }
    }

    if (!user.company || !user.name) {
        return { name: "profile", query: { redirect: to.fullPath === "/" ? "/apply" : to.fullPath } };
    }

    if (to.name === "index") {
        return { name: "user_info" };
    }

    return true;
});

router.afterEach(() => {
    document.body?.classList?.remove?.("is-nav-dragging");
});

export default router;
